/**
 * API Error Handling Layer for Microservices
 *
 * This module provides comprehensive error handling for a microservice architecture,
 * including retry logic, error categorization, and user-friendly error messages.
 *
 * Handles:
 * - 504 Gateway Timeout (service unreachable)
 * - 503 Service Unavailable (service down)
 * - 502 Bad Gateway (proxy issues)
 * - 429 Too Many Requests (rate limiting)
 * - Network errors (offline, DNS, etc.)
 * - Standard HTTP errors (400, 401, 403, 404, 500)
 */

import { AxiosError } from 'axios';

// =============================================================================
// ERROR TYPES
// =============================================================================

export enum ApiErrorCode {
  // Network & Infrastructure
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  BAD_GATEWAY = 'BAD_GATEWAY',
  RATE_LIMITED = 'RATE_LIMITED',

  // Authentication & Authorization
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  SESSION_EXPIRED = 'SESSION_EXPIRED',

  // Client Errors
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONFLICT = 'CONFLICT',

  // Server Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',

  // Generic
  UNKNOWN = 'UNKNOWN',
}

export interface ApiErrorDetails {
  code: ApiErrorCode;
  message: string;
  userMessage: string;
  httpStatus?: number;
  retryable: boolean;
  retryAfter?: number; // seconds
  details?: Record<string, unknown>;
  timestamp: string;
  requestId?: string;
  service?: string; // Which microservice failed
}

export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly userMessage: string;
  public readonly httpStatus?: number;
  public readonly retryable: boolean;
  public readonly retryAfter?: number;
  public readonly details?: Record<string, unknown>;
  public readonly timestamp: string;
  public readonly requestId?: string;
  public readonly service?: string;
  public readonly originalError?: Error;

  constructor(details: ApiErrorDetails, originalError?: Error) {
    super(details.message);
    this.name = 'ApiError';
    this.code = details.code;
    this.userMessage = details.userMessage;
    this.httpStatus = details.httpStatus;
    this.retryable = details.retryable;
    this.retryAfter = details.retryAfter;
    this.details = details.details;
    this.timestamp = details.timestamp;
    this.requestId = details.requestId;
    this.service = details.service;
    this.originalError = originalError;

    const errorConstructor = Error as ErrorConstructor & {
      captureStackTrace?: (targetObject: object, constructorOpt?: unknown) => void;
    };

    // Maintain proper stack trace in V8 runtimes.
    if (errorConstructor.captureStackTrace) {
      errorConstructor.captureStackTrace(this, ApiError);
    }
  }

  toJSON(): ApiErrorDetails {
    return {
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      httpStatus: this.httpStatus,
      retryable: this.retryable,
      retryAfter: this.retryAfter,
      details: this.details,
      timestamp: this.timestamp,
      requestId: this.requestId,
      service: this.service,
    };
  }
}

// =============================================================================
// ERROR PARSING
// =============================================================================

/**
 * NestJS standard error response format
 */
interface NestJSErrorResponse {
  statusCode: number;
  message: string | string[];
  error?: string;
  code?: string;
  retryAfterSeconds?: number;
  retryAfter?: number | string;
  errors?: Record<string, string[]>;
  timestamp?: string;
  path?: string;
}

function positiveSeconds(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number' ? Math.ceil(value) : Number.parseInt(String(value ?? ''), 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Extract service name from URL or error response
 */
function extractServiceName(url?: string): string | undefined {
  if (!url) return undefined;

  // Map URL prefixes to service names
  const serviceMap: Record<string, string> = {
    '/auth': 'Authentication Service',
    '/tenant': 'Tenant Service',
    '/hrms': 'HRMS Service',
    '/crm': 'CRM Service',
    '/attendance': 'Attendance Service',
    '/admin': 'Admin Service',
    '/notifications': 'Notifications Service',
  };

  for (const [prefix, name] of Object.entries(serviceMap)) {
    if (url.includes(prefix)) {
      return name;
    }
  }

  return 'API Gateway';
}

/**
 * Parse Axios error into standardized ApiError
 */
export function parseApiError(error: unknown): ApiError {
  const timestamp = new Date().toISOString();

  // Handle Axios errors
  if (error instanceof AxiosError) {
    const { response, request, config } = error;
    const url = config?.url;
    const service = extractServiceName(url);

    // Extract request ID from headers if available
    const requestId =
      response?.headers?.['x-correlation-id'] ||
      response?.headers?.['x-request-id'] ||
      response?.headers?.['x-trace-id'] ||
      undefined;

    // Network error (no response received)
    if (!response && request) {
      // Check for timeout
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return new ApiError(
          {
            code: ApiErrorCode.TIMEOUT,
            message: `Request timeout: ${url}`,
            userMessage: 'The request took too long to complete. Please try again.',
            retryable: true,
            retryAfter: 3,
            timestamp,
            requestId,
            service,
          },
          error,
        );
      }

      // Network error (offline, DNS, etc.)
      return new ApiError(
        {
          code: ApiErrorCode.NETWORK_ERROR,
          message: `Network error: ${error.message}`,
          userMessage: 'Unable to connect to the server. Please check your internet connection.',
          retryable: true,
          retryAfter: 5,
          timestamp,
          requestId,
          service,
        },
        error,
      );
    }

    // HTTP error response received
    if (response) {
      const status = response.status;
      const data = response.data as NestJSErrorResponse | undefined;
      const message = Array.isArray(data?.message)
        ? data.message.join(', ')
        : data?.message || error.message;

      // Extract retry-after header if present
      const retryAfterHeader = response.headers?.['retry-after'];
      const retryAfter =
        positiveSeconds(retryAfterHeader) ??
        positiveSeconds(data?.retryAfterSeconds) ??
        positiveSeconds(data?.retryAfter);

      switch (status) {
        case 400:
          return new ApiError(
            {
              code: data?.errors ? ApiErrorCode.VALIDATION_ERROR : ApiErrorCode.BAD_REQUEST,
              message,
              userMessage: data?.errors
                ? 'Please check your input and try again.'
                : message || 'Invalid request. Please check your input.',
              httpStatus: 400,
              retryable: false,
              details: data?.errors ? { validationErrors: data.errors } : undefined,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 401:
          return new ApiError(
            {
              code: ApiErrorCode.UNAUTHORIZED,
              message: 'Authentication required',
              userMessage: 'Please log in to continue.',
              httpStatus: 401,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 403:
          return new ApiError(
            {
              code: ApiErrorCode.FORBIDDEN,
              message: 'Access denied',
              userMessage: "You don't have permission to perform this action.",
              httpStatus: 403,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 404:
          return new ApiError(
            {
              code: ApiErrorCode.NOT_FOUND,
              message: `Resource not found: ${url}`,
              userMessage: 'The requested resource could not be found.',
              httpStatus: 404,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 409:
          return new ApiError(
            {
              code: ApiErrorCode.CONFLICT,
              message: message || 'Resource conflict',
              userMessage: message || 'This action conflicts with existing data.',
              httpStatus: 409,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 422:
          return new ApiError(
            {
              code: ApiErrorCode.VALIDATION_ERROR,
              message: message || 'Request could not be processed',
              userMessage:
                message || 'This request cannot be processed with the current configuration.',
              httpStatus: 422,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 429:
          return new ApiError(
            {
              code: ApiErrorCode.RATE_LIMITED,
              message: message || 'Rate limit exceeded',
              userMessage: retryAfter
                ? `Too many requests. Please try again in ${retryAfter} seconds.`
                : 'Too many requests. Please wait a moment and try again.',
              httpStatus: 429,
              retryable: true,
              retryAfter: retryAfter || 30,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 500:
          return new ApiError(
            {
              code: ApiErrorCode.INTERNAL_ERROR,
              message: `Internal server error: ${message}`,
              userMessage: 'An unexpected error occurred. Our team has been notified.',
              httpStatus: 500,
              retryable: true,
              retryAfter: 5,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 501:
          return new ApiError(
            {
              code: ApiErrorCode.NOT_IMPLEMENTED,
              message: 'Feature not implemented',
              userMessage: 'This feature is not yet available.',
              httpStatus: 501,
              retryable: false,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 502:
          return new ApiError(
            {
              code: ApiErrorCode.BAD_GATEWAY,
              message: `Bad gateway error: ${service || 'unknown service'}`,
              userMessage: `The ${service || 'service'} is temporarily unavailable. Please try again.`,
              httpStatus: 502,
              retryable: true,
              retryAfter: 10,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 503:
          return new ApiError(
            {
              code: ApiErrorCode.SERVICE_UNAVAILABLE,
              message: `Service unavailable: ${service || 'unknown service'}`,
              userMessage: `The ${service || 'service'} is currently down for maintenance. Please try again shortly.`,
              httpStatus: 503,
              retryable: true,
              retryAfter: retryAfter || 30,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        case 504:
          return new ApiError(
            {
              code: ApiErrorCode.GATEWAY_TIMEOUT,
              message: `Gateway timeout: ${service || 'unknown service'} not responding`,
              userMessage: `The ${service || 'service'} is taking too long to respond. Please try again.`,
              httpStatus: 504,
              retryable: true,
              retryAfter: 15,
              timestamp,
              requestId,
              service,
            },
            error,
          );

        default:
          return new ApiError(
            {
              code: ApiErrorCode.UNKNOWN,
              message: message || `HTTP ${status} error`,
              userMessage: 'An unexpected error occurred. Please try again.',
              httpStatus: status,
              retryable: status >= 500,
              retryAfter: status >= 500 ? 5 : undefined,
              timestamp,
              requestId,
              service,
            },
            error,
          );
      }
    }
  }

  // Handle ApiError (already parsed)
  if (error instanceof ApiError) {
    return error;
  }

  // Handle generic Error
  if (error instanceof Error) {
    return new ApiError(
      {
        code: ApiErrorCode.UNKNOWN,
        message: error.message,
        userMessage: 'An unexpected error occurred. Please try again.',
        retryable: false,
        timestamp,
      },
      error,
    );
  }

  // Handle unknown error types
  return new ApiError({
    code: ApiErrorCode.UNKNOWN,
    message: String(error),
    userMessage: 'An unexpected error occurred. Please try again.',
    retryable: false,
    timestamp,
  });
}

// =============================================================================
// RETRY UTILITIES
// =============================================================================

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number; // ms
  maxDelay: number; // ms
  backoffMultiplier: number;
  retryCondition?: (error: ApiError) => boolean;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  retryCondition: (error) => error.retryable,
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, config: RetryConfig, retryAfter?: number): number {
  if (retryAfter) {
    return retryAfter * 1000; // Convert seconds to ms
  }

  // Exponential backoff
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);

  // Add jitter (random 0-25% of delay)
  const jitter = exponentialDelay * 0.25 * Math.random();

  // Cap at max delay
  return Math.min(exponentialDelay + jitter, config.maxDelay);
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute a function with automatic retry on retryable errors
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {},
): Promise<T> {
  const finalConfig = { ...defaultRetryConfig, ...config };
  let lastError: ApiError | undefined;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const apiError = parseApiError(error);
      lastError = apiError;

      // Check if we should retry
      const shouldRetry =
        attempt < finalConfig.maxRetries &&
        (finalConfig.retryCondition ? finalConfig.retryCondition(apiError) : apiError.retryable);

      if (!shouldRetry) {
        throw apiError;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt, finalConfig, apiError.retryAfter);

      // Log retry attempt (in development)
      if (import.meta.env.DEV) {
        console.warn(
          `[API Retry] Attempt ${attempt + 1}/${finalConfig.maxRetries + 1} failed. ` +
            `Retrying in ${Math.round(delay / 1000)}s...`,
          {
            error: apiError.code,
            message: apiError.message,
          },
        );
      }

      await sleep(delay);
    }
  }

  throw (
    lastError ||
    new ApiError({
      code: ApiErrorCode.UNKNOWN,
      message: 'Max retries exceeded',
      userMessage: 'Unable to complete the request after multiple attempts.',
      retryable: false,
      timestamp: new Date().toISOString(),
    })
  );
}

// =============================================================================
// ERROR HANDLING UTILITIES
// =============================================================================

/**
 * Check if an error is a specific type
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Check if error is a network/infrastructure issue (retryable)
 */
export function isInfrastructureError(error: unknown): boolean {
  if (!isApiError(error)) return false;

  return [
    ApiErrorCode.NETWORK_ERROR,
    ApiErrorCode.TIMEOUT,
    ApiErrorCode.GATEWAY_TIMEOUT,
    ApiErrorCode.SERVICE_UNAVAILABLE,
    ApiErrorCode.BAD_GATEWAY,
    ApiErrorCode.RATE_LIMITED,
  ].includes(error.code);
}

/**
 * Check if error is an authentication issue
 */
export function isAuthError(error: unknown): boolean {
  if (!isApiError(error)) return false;

  return [ApiErrorCode.UNAUTHORIZED, ApiErrorCode.SESSION_EXPIRED].includes(error.code);
}

/**
 * Check if error is a validation issue
 */
export function isValidationError(error: unknown): boolean {
  if (!isApiError(error)) return false;

  return [ApiErrorCode.BAD_REQUEST, ApiErrorCode.VALIDATION_ERROR].includes(error.code);
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.userMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Get validation errors as a record
 */
export function getValidationErrors(error: unknown): Record<string, string[]> | undefined {
  if (isApiError(error) && error.details?.validationErrors) {
    return error.details.validationErrors as Record<string, string[]>;
  }
  return undefined;
}
